import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { doc, onSnapshot, updateDoc, arrayUnion } from "firebase/firestore";
import Header from "../components/Header";

export const SCAFFOLDING_SECTIONS = [
  {
    id: "basics",
    title: "00 · Basics",
    color: "#4a5568",
    questions: [
      "What is the title of the book?",
      "Who is the author?",
      "What type of book is this? (fiction, non-fiction, biography, fantasy, etc.)",
      "What is the main subject or topic of this book?",
      "Before reading, what do you think this book will be about?",
      "What are your first impressions after starting the book?",
      "What questions do you have before or as you begin reading?"
    ]
  },
  {
    id: "who",
    title: "01 · Who",
    color: "#4299e1",
    questions: [
      "Who is the main character or main person this book is about?",
      "Who are the important supporting characters or people?",
      "Are there any important groups, teams, families, or nations? Describe them.",
      "What are the key relationships between characters or people?",
      "What do the main characters want or what are they trying to achieve?",
      "How do the main characters change throughout the book?",
      "What did the main character achieve or fail to achieve?"
    ]
  },
  {
    id: "what",
    title: "02 · What",
    color: "#ed8936",
    questions: [
      "What is the main plot or main argument of the book?",
      "What are the most important events or ideas in the book?",
      "What problems or conflicts appear in the book?",
      "What important decisions do characters or people make?",
      "What are the consequences of those decisions?",
      "What is the turning point or most important moment in the book?",
      "What are the most important facts or ideas you want to remember?"
    ]
  },
  {
    id: "where",
    title: "03 · Where",
    color: "#48bb78",
    questions: [
      "Where does the main story or event take place?",
      "What other important places appear in the book?",
      "Does the location change during the book? Describe how.",
      "Why does the setting or location matter to the story?",
      "What is the mood or atmosphere of the main setting?"
    ]
  },
  {
    id: "when",
    title: "04 · When",
    color: "#38b2ac",
    questions: [
      "What time period does this book take place in?",
      "Describe the timeline of the most important events in order.",
      "What happened before, during, and after the most important event?",
      "How much time passes during the book?",
      "Why does the timing of events matter to the story?"
    ]
  },
  {
    id: "why",
    title: "05 · Why",
    color: "#805ad5",
    questions: [
      "Why did the most important events happen?",
      "Why did the main characters act the way they did?",
      "Why do you think the author wrote this book?",
      "What are the main themes or big ideas of the book?",
      "What lessons or warnings does the book offer?",
      "What caused the main problem or conflict?",
      "What big questions does this book raise that you are still thinking about?"
    ]
  },
  {
    id: "how",
    title: "06 · How",
    color: "#d69e2e",
    questions: [
      "What was the strategy or plan the main character or group used?",
      "What methods or tools did they use?",
      "What obstacles did they face?",
      "How were the main problems solved?",
      "How did the main character change or grow?",
      "How does the author explain or prove the main ideas?",
      "How would you describe the author's writing style and tone?"
    ]
  },
  {
    id: "evidence",
    title: "07 · Evidence",
    color: "#e53e3e",
    questions: [
      "Write down an important quote from the book and the page number.",
      "What page numbers should you remember and why?",
      "Describe an important scene in detail.",
      "What is the most important fact or piece of information in the book?",
      "What evidence will you use to support your final opinion of this book?"
    ]
  },
  {
    id: "questions",
    title: "08 · Questions",
    color: "#319795",
    questions: [
      "What questions do you have while reading?",
      "What parts of the book do you not understand yet?",
      "What do you predict will happen next?",
      "What predictions did you make that turned out to be wrong?",
      "What question would you most want to ask the author?"
    ]
  },
  {
    id: "reflections",
    title: "09 · Reflections",
    color: "#dd6b20",
    questions: [
      "What surprised you most about this book?",
      "What did you like about this book?",
      "What did you not like about this book?",
      "What would you change about the book if you could?",
      "What did you agree with in this book?",
      "What did you disagree with in this book?",
      "What is the most important thing you learned from this book?",
      "How would you describe this book to a friend in five sentences?"
    ]
  },
  {
    id: "report",
    title: "10 · Report",
    color: "#2f855a",
    questions: [
      "Write one sentence that summarizes the entire book.",
      "Summarize the beginning, middle, and end of the book.",
      "Who are the main characters or what are the main ideas?",
      "What is the main conflict or main argument?",
      "What is the theme, lesson, or purpose of the book?",
      "What is the best evidence or example from the book?",
      "What is your personal opinion of this book?",
      "Would you recommend this book and to whom? Why?"
    ]
  }
];

export default function Outline() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("basics");

  // Keep a ref to the last-saved state so we can diff on save
  const savedNotesRef = useRef({});

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "books", id), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setBook({ id: snap.id, ...data });
        setNotes(data.scaffolding || {});
        savedNotesRef.current = data.scaffolding || {};
      }
    });
    return unsubscribe;
  }, [id]);

  async function saveNotes() {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const historyEntries = [];

      // Diff current notes against last saved state
      for (const section of SCAFFOLDING_SECTIONS) {
        const sectionId = section.id;
        const currentSection = notes[sectionId] || {};
        const savedSection = savedNotesRef.current[sectionId] || {};

        section.questions.forEach((_, questionIndex) => {
          const currentValue = (currentSection[questionIndex] || "").trim();
          const savedValue = (savedSection[questionIndex] || "").trim();

          // Only log if something actually changed and the new value isn't empty
          if (currentValue !== savedValue && currentValue !== "") {
            historyEntries.push({
              savedAt: now,
              section: sectionId,
              questionIndex,
              value: currentValue
            });
          }
        });
      }

      const updatePayload = { scaffolding: notes };

      // Only write history if there are actual changes
      if (historyEntries.length > 0) {
        updatePayload.scaffoldingHistory = arrayUnion(...historyEntries);
      }

      await updateDoc(doc(db, "books", id), updatePayload);

      // Update the ref to reflect what's now saved
      savedNotesRef.current = notes;

      alert("Notes saved!");
    } catch (err) {
      console.error(err);
      alert("Failed to save. Try again.");
    }
    setSaving(false);
  }

  function updateNote(sectionId, questionIndex, value) {
    setNotes((prev) => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [questionIndex]: value
      }
    }));
  }

  function getSectionProgress(sectionId) {
    const section = SCAFFOLDING_SECTIONS.find((s) => s.id === sectionId);
    if (!section) return 0;
    const sectionNotes = notes[sectionId] || {};
    const answered = Object.values(sectionNotes).filter((v) => v.trim() !== "").length;
    return Math.round((answered / section.questions.length) * 100);
  }

  if (!book) return <div style={styles.loading}>Loading...</div>;

  const activeData = SCAFFOLDING_SECTIONS.find((s) => s.id === activeSection);

  return (
    <div style={styles.container}>
      <Header />

      <div style={styles.content}>
        {/* Section Tabs */}
        <div style={styles.tabsContainer}>
          {SCAFFOLDING_SECTIONS.map((section) => {
            const progress = getSectionProgress(section.id);
            return (
              <div
                key={section.id}
                style={{
                  ...styles.tab,
                  borderColor: activeSection === section.id ? section.color : "#e2e8f0",
                  backgroundColor: activeSection === section.id ? section.color : "white"
                }}
                onClick={() => setActiveSection(section.id)}
              >
                <p style={{
                  ...styles.tabTitle,
                  color: activeSection === section.id ? "white" : "#4a5568"
                }}>
                  {section.title}
                </p>
                <div style={styles.tabProgressBar}>
                  <div style={{
                    ...styles.tabProgressFill,
                    width: `${progress}%`,
                    backgroundColor: activeSection === section.id ? "rgba(255,255,255,0.5)" : section.color
                  }} />
                </div>
                <p style={{
                  ...styles.tabPercent,
                  color: activeSection === section.id ? "white" : "#a0aec0"
                }}>
                  {progress}%
                </p>
              </div>
            );
          })}
        </div>

        {/* Active Section Questions */}
        {activeData && (
          <div style={styles.card}>
            <h2 style={{ ...styles.sectionTitle, color: activeData.color }}>
              {activeData.title}
            </h2>
            {activeData.questions.map((question, i) => (
              <div key={i} style={styles.questionBlock}>
                <label style={styles.question}>{question}</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Write your answer here..."
                  value={(notes[activeSection] && notes[activeSection][i]) || ""}
                  onChange={(e) => updateNote(activeSection, i, e.target.value)}
                  rows={3}
                />
              </div>
            ))}
          </div>
        )}

        <button style={styles.saveBottomBtn} onClick={saveNotes} disabled={saving}>
          {saving ? "Saving..." : "💾 Save Notes"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" },
  content: { padding: 24, maxWidth: 700, margin: "0 auto" },
  tabsContainer: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 },
  tab: { padding: 12, borderRadius: 10, border: "2px solid", cursor: "pointer", textAlign: "center" },
  tabTitle: { fontSize: 12, fontWeight: "bold", margin: "0 0 6px 0" },
  tabProgressBar: { height: 4, backgroundColor: "#e2e8f0", borderRadius: 2, marginBottom: 4 },
  tabProgressFill: { height: "100%", borderRadius: 2, transition: "width 0.3s ease" },
  tabPercent: { fontSize: 11, margin: 0 },
  card: { backgroundColor: "white", borderRadius: 12, padding: 24, marginBottom: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" },
  sectionTitle: { fontSize: 22, marginTop: 0, marginBottom: 20 },
  questionBlock: { marginBottom: 20 },
  question: { display: "block", fontWeight: "bold", color: "#2d3748", marginBottom: 8, fontSize: 15 },
  textarea: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 15, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" },
  saveBottomBtn: { width: "100%", padding: 16, backgroundColor: "#805ad5", color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer", marginBottom: 24 }
};